import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Heading } from '@swyftx/aviary/atoms/Typography';
import { Body } from '@swyftx/aviary/atoms/Typography';
import { types } from '@swyftx/marketing-utils';

import Progress from './Progress';
import { RewardCardProgress } from './RewardCardProgress';

interface ContentProps {
  reward: types.UserAssetReward;
  redemption?: types.UserAssetRedemption;
}

interface ProcessedRequirement {
  requirement: types.UserAssetRedemptionRequirement;
  linkedRequirement?: types.UserAssetRedemptionRequirement;
}

export default function Content({ reward, redemption, children }: PropsWithChildren<ContentProps>) {
  const { t } = useTranslation('rewards', { keyPrefix: 'rewardCard' });

  // Process requirements to handle KYC1 and GreenID together
  const processedRequirements = useMemo(() => {
    if (!redemption) return [] as ProcessedRequirement[];

    const { requirements } = redemption;

    const hasKyc1 = requirements.some((req) => req.trigger === types.AssetRewardQualificationTrigger.Kyc1Complete);
    const hasGreenId = requirements.some(
      (req) => req.trigger === types.AssetRewardQualificationTrigger.GreenIdComplete,
    );
    const hasKycAndGreenId = hasKyc1 && hasGreenId;

    return requirements
      .map((requirement) => {
        if (hasKycAndGreenId && requirement.trigger === types.AssetRewardQualificationTrigger.GreenIdComplete) {
          return null;
        }
        let linkedRequirement: types.UserAssetRedemptionRequirement | undefined = undefined;
        if (hasKycAndGreenId && requirement.trigger === types.AssetRewardQualificationTrigger.Kyc1Complete) {
          linkedRequirement = requirements.find(
            (req) => req.trigger === types.AssetRewardQualificationTrigger.GreenIdComplete,
          );
        }

        return { requirement, linkedRequirement } as ProcessedRequirement;
      })
      .filter(Boolean) as ProcessedRequirement[];
  }, [redemption]);

  return (
    <div className='grid gap-32 md:grid-cols-[1fr_1fr]'>
      <div className='align-start grid grid-rows-[auto_1fr] justify-start gap-12'>
        <Body>{reward?.description}</Body>
        {/* TODO Add these in when backend API is done, dont forget to add another 1fr to the grid rows */}
        {/* <Body>
          <span className='font-bold'>X</span> of <span className='font-bold'>XXX</span> Rewards redeemed
        </Body> */}
        {children}
      </div>
      <div className='grid grid-rows-[auto_auto_1fr] gap-12'>
        <Heading size='h4' weight='light'>
          {String(reward.hasPayout ? t('requirementsToRedeem') : t('entryRequirements'))}
        </Heading>
        <Progress requirements={redemption?.requirements} />
        <div className='grid content-start gap-8 px-8'>
          {processedRequirements.map(({ requirement, linkedRequirement }) => (
            <RewardCardProgress
              key={requirement.trigger}
              redemptionRequirement={requirement}
              linkedRequirement={linkedRequirement}
            />
          ))}
          {!redemption &&
            reward?.qualifications?.map((qualification) => (
              <RewardCardProgress key={qualification.trigger} redemptionRequirement={qualification} />
            ))}
        </div>
      </div>
    </div>
  );
}
